import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  addProtocol, Map as MlMap, Marker, NavigationControl, Popup, ScaleControl, setWorkerUrl,
  type MapLayerMouseEvent,
} from 'maplibre-gl'
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { Protocol } from 'pmtiles'
import { List, X } from 'lucide-react'
import 'maplibre-gl/dist/maplibre-gl.css'

import { buildRasterStyle, createGoogle2dSession, rasterBasemap, type RasterMode } from '../map/basemaps'
import type { PhotorealHandle } from '../map/photoreal'
import { useFleet } from '../state/store'
import {
  addBusRouteLayers, setBusRouteData, routeLatLngPath, ROUTE_LINE_COLOR, LYR_BUS_SEGMENT_LINE,
} from '../map/layers/busRoute'
import { BUS_ICON_PIXEL_RATIO, BUS_MODELS, busIconImageData, busModel } from './busIcons'
import { coastBusPosition } from './MbmtLiveMap'
import { fetchBusRouteDetail } from './useBusApi'
import type { BusBoardRow, BusBreakdown, BusRouteDetail, BusRouteSummary } from './types'

/** No update in this long reads as "stale", not confidently moving or idle -- same
 *  "absence of data is not evidence of a problem" rule this file already applies to
 *  segment tone, just for a vehicle instead of a route segment. */
const VEHICLE_STALE_MS = 5 * 60_000

export type VehicleStatus = 'down' | 'moving' | 'idle' | 'stale'

/** Same palette MBMT's live map uses (BUS_STATUS_STYLE in MbmtLiveMap.tsx, exported there
 *  for its own legend's tests but not imported here) -- duplicated rather than imported
 *  (coupling two page files any tighter than the shared busIcons.ts/coastBusPosition
 *  already does isn't worth it for four colour pairs), but kept byte-for-byte identical
 *  on purpose: a bus icon here should mean the same thing at a glance as one on the MBMT
 *  desk, per direct instruction. */
const BUS_STATUS_STYLE: Record<VehicleStatus, { fill: string; edge: string }> = {
  down: { fill: '#F43F5E', edge: '#9F1239' },
  moving: { fill: '#10B981', edge: '#047857' },
  idle: { fill: '#F59E0B', edge: '#B45309' },
  stale: { fill: '#94A3B8', edge: '#64748B' },
}

function vehicleStatus(row: BusBoardRow, down: boolean): VehicleStatus {
  if (down) return 'down'
  if (row.updated_ts == null || Date.now() - row.updated_ts > VEHICLE_STALE_MS) return 'stale'
  return row.speed_kph != null && row.speed_kph > 3 ? 'moving' : 'idle'
}

function vehiclePopupHtml(row: BusBoardRow, status: VehicleStatus): string {
  const speed = row.speed_kph != null ? `${row.speed_kph.toFixed(0)} kph` : '—'
  const soc = row.soc_pct != null ? `${row.soc_pct.toFixed(0)}%` : '—'
  const heading = row.heading_deg != null ? `${row.heading_deg.toFixed(0)}°` : '—'
  const statusLabel = status === 'down' ? 'Breakdown reported'
    : status === 'moving' ? 'Moving' : status === 'idle' ? 'Idle' : 'Stale (no recent update)'
  const place = row.depot_id
    ? `Near ${row.depot_id}`
    : row.status === 'out_on_route' ? 'Out on route' : 'No recent signal'
  const updated = row.updated_ts != null
    ? new Date(row.updated_ts).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })
    : '—'
  // EDMS duty join (serving/app.py's _nagpur_duty_for/_nagpur_current_trip) -- a plate
  // with no duty row today shows every one of these as '—', not omitted, same "null,
  // not omitted" convention the board API itself follows (AC-3).
  const driver = row.driver_name ?? '—'
  const conductor = row.conductor_id ?? '—'
  const schedule = row.schedule_code
    ? `${row.schedule_code}${row.schedule_shift ? ` (${row.schedule_shift})` : ''}`
    : '—'
  const trip = row.current_trip
    ? `${row.current_trip.from_stage ?? '?'} → ${row.current_trip.to_stage ?? '?'}`
    : '—'
  // SPEC.md's own edge case: two schedules can claim the same plate/day. The rows above
  // only ever show the single primary pick (same AC-3/AC-4-locked value the board API's
  // own scalar fields carry) -- this line is where the SECOND schedule actually becomes
  // visible instead of silently disappearing from the popup.
  const otherSchedules = row.schedules.filter((s) => s.trip_id !== row.current_trip?.trip_id && s.schedule_code !== row.schedule_code)
  const otherScheduleLine = row.schedules.length > 1
    ? `<div style="margin-top:4px;color:#8794A7">+${row.schedules.length - 1} more schedule${row.schedules.length > 2 ? 's' : ''}: ` +
      `${(otherSchedules.length ? otherSchedules : row.schedules.slice(1)).map((s) => s.schedule_code ?? '—').join(', ')}</div>`
    : ''
  return (
    `<div style="font:12px 'JetBrains Mono',monospace">` +
    `<div style="font-weight:600;margin-bottom:4px">${row.vehicle_id}</div>` +
    `<div>${statusLabel} · ${place}</div>` +
    `<div style="margin-top:4px;display:grid;grid-template-columns:auto auto;gap:1px 10px">` +
    `<span style="color:#8794A7">Speed</span><span>${speed}</span>` +
    `<span style="color:#8794A7">SOC</span><span>${soc}</span>` +
    `<span style="color:#8794A7">Heading</span><span>${heading}</span>` +
    `<span style="color:#8794A7">Updated</span><span>${updated} IST</span>` +
    `<span style="color:#8794A7">Driver</span><span>${driver}</span>` +
    `<span style="color:#8794A7">Conductor</span><span>${conductor}</span>` +
    `<span style="color:#8794A7">Schedule</span><span>${schedule}</span>` +
    `<span style="color:#8794A7">Current trip</span><span>${trip}</span>` +
    `</div>` +
    otherScheduleLine +
    `</div>`
  )
}

/** This page's own basemap mode -- NOT the shared useFleet.basemapMode the truck corridor
 *  uses. That field's 'vector' default is the self-hosted PMTiles archive cut to the
 *  Dhar-Jhulwania-Dhule bbox (map/style.ts's CORRIDOR_BOUNDS); a Nagpur depot is nowhere
 *  near it, so 'vector' is not a valid mode here at all -- this page was always raster-only
 *  (map/basemaps.ts's catalogue, not bbox-limited). What IS shared with the rest of the
 *  console: the mapsKey/photorealAvailable/google2dAvailable session state in useFleet,
 *  read here instead of this component doing its own redundant /api/config fetch. OSM
 *  stays the default and is always available with no key -- Google 2D is opt-in via the
 *  toggle below (auto-promoted once a session exists, with a 3-strike tile-failure
 *  fallback back to osm, same as MapCanvas.tsx's own default+fallback logic). */
type NagpurBaseMode = RasterMode

/**
 * Depot coordinates. NOT a geofence -- there is no polygon boundary for either depot
 * anywhere in the schema (the `geofence` table is Ultratech-corridor-only). Wathoda's is
 * the literal "Wathoda Depot" stop in fleet.bus_route_stop; Khapri has no named depot stop,
 * so "Khapri Naka" (route 88 UP, stop 1 -- the closest landmark in that same real data) is
 * used instead. Both cross-validated against independent parked-vehicle GPS clustering
 * (queried 2026-09-25) within ~300m.
 */
const DEPOTS: { id: string; name: string; lat: number; lon: number }[] = [
  { id: 'wathoda', name: 'Wathoda Depot', lat: 21.13166, lon: 79.15255 },
  { id: 'khapri', name: 'Khapri Naka (depot)', lat: 21.05626, lon: 79.05433 },
]

let protocolRegistered = false
function ensureProtocol(): void {
  if (protocolRegistered) return
  setWorkerUrl(workerUrl)
  const protocol = new Protocol()
  addProtocol('pmtiles', protocol.tile)
  protocolRegistered = true
}

/** Lazy-loads the actual Google Maps JavaScript API (a different product from the Map
 *  Tiles API session flow map/basemaps.ts already uses for the 2D/photoreal basemap
 *  options) -- this is the only way to get a real TrafficLayer, which Google does not
 *  expose as a standalone tile endpoint. Loaded once, on first route selection, not
 *  eagerly on page load: the overwhelming majority of visits never open a route, and this
 *  is a second billed Google product on top of the basemap's own session. No
 *  @google.maps types package is installed for this single narrow use, hence the `any`
 *  below -- scoped to this one loader and the card that calls it, nowhere else. */
let mapsJsPromise: Promise<any | null> | null = null // eslint-disable-line @typescript-eslint/no-explicit-any
function loadGoogleMapsJs(key: string): Promise<any | null> { // eslint-disable-line @typescript-eslint/no-explicit-any
  const w = window as unknown as { google?: { maps?: unknown } }
  if (w.google?.maps) return Promise.resolve(w.google)
  if (mapsJsPromise) return mapsJsPromise
  mapsJsPromise = new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly`
    script.async = true
    script.onload = () => resolve((window as unknown as { google?: unknown }).google ?? null)
    script.onerror = () => { mapsJsPromise = null; resolve(null) }
    document.head.appendChild(script)
  })
  return mapsJsPromise
}

/** Takes over the ENTIRE main map area -- not a corner card -- with a real
 *  google.maps.Map showing live TrafficLayer plus this one route's own road-following
 *  path, fitted tightly to just its stops. Direct instruction, twice: "traffic must be
 *  shown on main map, only for the selected route, nothing else" and "this is not real,
 *  showing red still" (the MapLibre route line's old speed-divergence colouring, fixed
 *  separately in busRoute.ts, was being mistaken for fake traffic sitting right next to
 *  this genuine one). The underlying MapLibre live-bus view is hidden, not destroyed,
 *  while this is open -- see the mapsKey-gated className toggle in the main render
 *  below -- so closing the route is instant, no remount. */
function RouteTrafficView({
  route, detail, mapsKey, onClose,
}: {
  route: BusRouteSummary
  detail: BusRouteDetail
  mapsKey: string
  onClose: () => void
}): JSX.Element {
  const mapEl = useRef<HTMLDivElement>(null)
  const [trafficError, setTrafficError] = useState(false)
  // Off by default -- stop markers used to always render the instant a route was
  // selected, which read as clutter on top of the traffic view. Direct instruction:
  // "show stops on routes only when asked. Keep a option to show stops also."
  const [showStops, setShowStops] = useState(false)
  const showStopsRef = useRef(showStops)
  useEffect(() => { showStopsRef.current = showStops }, [showStops])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- no @google.maps types
  // package for this single narrow use, same reason loadGoogleMapsJs returns `any`.
  const stopMarkersRef = useRef<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gmapRef = useRef<any>(null)

  useEffect(() => {
    if (!mapEl.current || detail.stops.length === 0) return
    let cancelled = false
    setTrafficError(false)
    void loadGoogleMapsJs(mapsKey).then((g) => {
      if (cancelled) return
      if (!g || !mapEl.current) { setTrafficError(true); return }
      const lats = detail.stops.map((s) => s.lat)
      const lons = detail.stops.map((s) => s.lon)
      const bounds = new g.maps.LatLngBounds(
        { lat: Math.min(...lats), lng: Math.min(...lons) },
        { lat: Math.max(...lats), lng: Math.max(...lons) },
      )
      const gmap = new g.maps.Map(mapEl.current, {
        center: bounds.getCenter(),
        zoom: 13,
        zoomControl: true,
        gestureHandling: 'greedy',
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      })
      gmap.fitBounds(bounds, 48)
      gmapRef.current = gmap
      new g.maps.TrafficLayer().setMap(gmap)

      // Same flat ROUTE_LINE_COLOR the MapLibre fallback line uses -- one route should
      // look like one route regardless of which map engine is drawing it. The real
      // road-following path (routeLatLngPath's seg.polyline-else-straight fallback),
      // not a stop-to-stop straight line.
      const path = routeLatLngPath(detail).map(([lat, lng]) => ({ lat, lng }))
      const polyline = new g.maps.Polyline({
        path, map: gmap, strokeColor: ROUTE_LINE_COLOR, strokeWeight: 5, strokeOpacity: 0.95,
      })
      // Sequence number as the marker's own label, not a separate list -- stops must be
      // readable straight off the map, not out of a side panel. Direct instruction:
      // "everything must be visible on main map only." Visibility itself starts off
      // (showStopsRef, read once at creation) per the later "only when asked" instruction
      // -- the Stops toggle below flips .setMap() on these same marker instances rather
      // than re-creating them.
      const stopMarkers = detail.stops.map((s) => new g.maps.Marker({
        position: { lat: s.lat, lng: s.lon },
        map: showStopsRef.current ? gmap : null,
        title: s.stop_name,
        label: {
          text: String(s.sequence_no), fontSize: '9px', fontWeight: '700', color: ROUTE_LINE_COLOR,
        },
        icon: {
          path: g.maps.SymbolPath.CIRCLE, scale: 8,
          fillColor: '#FFFFFF', fillOpacity: 1, strokeColor: ROUTE_LINE_COLOR, strokeWeight: 2,
        },
      }))
      stopMarkersRef.current = stopMarkers

      // Confirmed live (2026-09-25): a google.maps.Map constructed the instant its
      // container enters the DOM can render as a tiny/garbled fragment of itself instead
      // of the full map -- a well-known Google Maps embed issue when the container's
      // final layout size isn't settled yet at construction time. 'idle' fires once the
      // map has finished its first real render; triggering 'resize' + re-fitting bounds
      // at that point is the standard fix. A second, delayed trigger covers the rarer
      // case where the container itself keeps resizing after 'idle'.
      const idle = g.maps.event.addListenerOnce(gmap, 'idle', () => {
        g.maps.event.trigger(gmap, 'resize')
        gmap.fitBounds(bounds, 48)
      })
      const settleTimer = window.setTimeout(() => {
        g.maps.event.trigger(gmap, 'resize')
        gmap.fitBounds(bounds, 48)
      }, 300)
      return () => {
        g.maps.event.removeListener(idle)
        window.clearTimeout(settleTimer)
        polyline.setMap(null)
        for (const m of stopMarkers) m.setMap(null)
        stopMarkersRef.current = []
      }
    })
    return () => { cancelled = true }
  }, [mapsKey, detail])

  // Toggling the Stops button flips .setMap() on the same marker instances the effect
  // above already created -- no map/route re-fetch, no flicker.
  useEffect(() => {
    for (const m of stopMarkersRef.current) m.setMap(showStops ? gmapRef.current : null)
  }, [showStops])

  return (
    <div className="absolute inset-0 z-10 bg-white">
      {trafficError ? (
        <div className="flex h-full items-center justify-center text-[13px] text-[#9F1239]">
          Live traffic failed to load.
        </div>
      ) : (
        <div ref={mapEl} className="h-full w-full bg-[#F1F5F9]" />
      )}

      <div className="absolute left-3 top-3 z-10 flex items-center gap-2 rounded-[10px] border border-[rgba(15,23,42,0.12)] bg-white px-3 py-2 shadow-[0_4px_16px_rgba(15,23,42,0.18)]">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] bg-[#2563EB] font-mono text-[11px] font-bold text-white">
          {route.route_number.length > 3 ? route.route_number.slice(0, 3) : route.route_number}
        </span>
        <span className="max-w-[280px] truncate text-[12px] font-semibold text-[#0F172A]">
          {route.display_name}
        </span>
        <button
          type="button"
          onClick={() => setShowStops((v) => !v)}
          aria-pressed={showStops}
          title={showStops ? 'Hide stops' : `Show ${detail.stops.length} stops`}
          className={clsx(
            'ml-1 shrink-0 rounded-[6px] px-2 py-1 text-[10.5px] font-semibold transition-colors',
            showStops
              ? 'bg-[#2563EB] text-white'
              : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]',
          )}
        >
          Stops
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close route detail"
          className="shrink-0 rounded p-0.5 text-[#94A3B8] transition-colors hover:bg-[#F8FAFC] hover:text-[#475569]"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  )
}

/** On-demand route picker -- NOT a permanently-docked sidebar. Opens as a floating overlay
 *  when the "Routes" pill (top-left) is clicked, closes on selection. Direct instruction:
 *  a fixed beside-map panel is not wanted; the list itself is, but only surfaced on
 *  click. Grouped by depot, same grouping the earlier docked panel used. */
function RouteListOverlay({
  routes, onPick, onClose,
}: {
  routes: BusRouteSummary[]
  onPick: (routeId: string) => void
  onClose: () => void
}): JSX.Element {
  const byDepot = new Map<string, BusRouteSummary[]>()
  for (const r of routes) {
    const list = byDepot.get(r.depot_name) ?? []
    list.push(r)
    byDepot.set(r.depot_name, list)
  }

  return (
    <div className="absolute left-3 top-3 z-10 flex max-h-[calc(100%-24px)] w-[280px] flex-col overflow-hidden rounded-[12px] border border-[rgba(15,23,42,0.12)] bg-white shadow-[0_4px_16px_rgba(15,23,42,0.18)]">
      <div className="flex shrink-0 items-center gap-2 border-b border-[rgba(15,23,42,0.06)] px-3 py-2">
        <span className="text-[12px] font-semibold text-[#0F172A]">Routes</span>
        <span className="text-[11px] font-medium text-[#94A3B8]">{routes.length}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close route list"
          className="ml-auto shrink-0 rounded p-0.5 text-[#94A3B8] transition-colors hover:bg-[#F8FAFC] hover:text-[#475569]"
        >
          <X size={14} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {[...byDepot.entries()].map(([depotName, group]) => (
          <div key={depotName}>
            <div className="sticky top-0 z-10 border-b border-[rgba(15,23,42,0.06)] bg-white/95 px-3 py-1.5 backdrop-blur">
              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[#64748B]">
                {depotName}
              </span>
              <span className="ml-1.5 text-[10px] text-[#94A3B8]">{group.length}</span>
            </div>
            {group.map((r) => (
              <button
                key={r.route_id}
                type="button"
                onClick={() => onPick(r.route_id)}
                className="flex w-full items-center gap-2 border-b border-[rgba(15,23,42,0.05)] px-3 py-2 text-left transition-colors hover:bg-[#F8FAFC]"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] bg-[#EEF2F7] font-mono text-[10px] font-bold text-[#334155]">
                  {r.route_number.length > 3 ? r.route_number.slice(0, 3) : r.route_number}
                </span>
                <span className="min-w-0 flex-1 truncate text-[11.5px] font-medium text-[#0F172A]">
                  {r.display_name}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const SRC_NAGPUR_BUSES = 'nagpur-buses'
const LYR_NAGPUR_PULSE = 'nagpur-bus-pulse'
const LYR_NAGPUR_SELECTED_RING = 'nagpur-bus-selected-ring'
const LYR_NAGPUR_BUSES = 'nagpur-buses-icon'
const LYR_NAGPUR_BUS_LABELS = 'nagpur-bus-labels'

// NAGPUR_MAX_COAST_S, not MBMT's own 12s: this page has no route-stops fetch and no
// per-vehicle next-stop clamp at all (MbmtLiveMap.tsx's capCoastAgeToStop has nothing
// to call here -- a vehicle's assigned route isn't resolvable from site_duty_trip's
// schedule_code alone; see fleet.bus_route_stop's route_id, e.g. "khapri-208-up":
// matching that unambiguously needs both the depot prefix and direction, neither of
// which schedule_code's bare route number disambiguates on its own). Confirmed live:
// without a stop to clamp against, 12s of straight-line coast at city speed overshoots
// a stop by 100m+ and holds there for the rest of the ~60s gap -- "passing very fast,
// not stopping on stops." A much smaller cap can't fix that precisely (it still can't
// know where the stop is), but it bounds how far wrong the guess can run: at 30 kph,
// 3s is ~25m of drift instead of 12s's ~100m.
const NAGPUR_MAX_COAST_S = 3

export function BusRouteMap(
  { routes, focusVehicleId, breakdowns, board }:
  {
    routes: BusRouteSummary[]
    focusVehicleId?: string | null
    breakdowns?: BusBreakdown[]
    /** NagpurBusOpsShell's own board poll (FLEET_POLL_MS) -- see the effect below that
     *  consumes it for why this map no longer fetches its own copy. */
    board?: BusBoardRow[]
  },
): JSX.Element {
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)
  const popupRef = useRef<Popup | null>(null)
  const styleReady = useRef(false)
  const busIconsRegistered = useRef(false)

  const photorealRef = useRef<PhotorealHandle | null>(null)
  const [view3D, setView3D] = useState(false)
  const [attribution, setAttribution] = useState<string[]>([])
  const [mapLoaded, setMapLoaded] = useState(false)
  // Bumped on every style load, not just the first -- see onStyleLoad's own comment on
  // why mapLoaded alone (a boolean that goes true -> true on every restyle) cannot be
  // what re-triggers a redraw after setStyle() wipes the map's sources.
  const [styleVersion, setStyleVersion] = useState(0)
  // Shared with the rest of the console (useFleet), NOT a local /api/config fetch -- see
  // this file's own header comment on why a duplicate fetch here was the actual bug.
  const mapsKey = useFleet((s) => s.mapsKey)
  const photorealAvailable = useFleet((s) => s.photorealAvailable)
  const google2dAvailable = useFleet((s) => s.google2dAvailable)
  const [baseMode, setBaseMode] = useState<NagpurBaseMode>('osm')
  // The construct-only effect below registers its 'error' handler once and would otherwise
  // close over baseMode's initial value forever -- same reason baseModeRef exists.
  const baseModeRef = useRef<NagpurBaseMode>('osm')
  useEffect(() => { baseModeRef.current = baseMode }, [baseMode])
  const google2dFailures = useRef(0)

  // Route detail is fetched on demand (one route at a time, when picked from the list),
  // not prefetched in bulk for every route -- a 70+-route eager fetch was both wasteful
  // and what made "every route drawn at once" possible in the first place, which is
  // exactly the clutter that was removed. Cached by route_id so re-selecting an
  // already-viewed route is instant.
  const detailCacheRef = useRef<Map<string, BusRouteDetail>>(new Map())
  const [cacheVersion, setCacheVersion] = useState(0)
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null)
  const [routeListOpen, setRouteListOpen] = useState(false)

  // Same session-creation effect as MapCanvas.tsx: eager, idempotent (skipped once
  // google2dAvailable is already true from whichever map mounted first), fire-and-forget.
  // A visitor landing directly on /bus-ops-nagpur without ever loading the truck view is
  // the reason this page cannot just assume MapCanvas.tsx already did this.
  useEffect(() => {
    if (!mapsKey || useFleet.getState().google2dAvailable) return
    let live = true
    void createGoogle2dSession(mapsKey).then((ok) => {
      if (live && ok) useFleet.getState().setGoogle2dAvailable(true)
    })
    return () => { live = false }
  }, [mapsKey])

  // Promote to google2d once available -- only if baseMode is still the untouched 'osm'
  // default, same "never override a real choice" rule MapCanvas.tsx's own promotion uses.
  useEffect(() => {
    if (google2dAvailable) setBaseMode((m) => (m === 'osm' ? 'google2d' : m))
  }, [google2dAvailable])

  useEffect(() => {
    if (!container.current) return
    ensureProtocol()
    // Always constructs with 'osm', regardless of baseMode's current value -- google2d
    // needs an async session first, which may not have resolved yet at construction time.
    // The restyle effect below promotes to google2d once baseMode actually changes.
    const basemap = rasterBasemap('osm')
    if (!basemap) return
    const map = new MlMap({
      container: container.current,
      style: buildRasterStyle(basemap),
      center: [79.09, 21.15], // Nagpur -- a reasonable default before any route selects
      zoom: 11,
      attributionControl: { compact: true },
    })
    mapRef.current = map
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left')

    // Re-run after EVERY style load, not just the first -- setStyle() (the restyle effect
    // below) wipes every source/layer a prior style added, same reason MapCanvas.tsx tracks
    // styleReady/reruns its own onStyle. Depot pin Markers are NOT part of the style system
    // (plain DOM elements MapLibre never touches on setStyle), so they're added once and
    // simply survive every later restyle -- no re-add needed. The bus icon IMAGES
    // (map.addImage) DO need re-registering on every restyle, same as the layers/sources
    // below -- a fresh style has none of the previous style's images either.
    let depotsPlaced = false
    const onStyleLoad = (): void => {
      addBusRouteLayers(map)

      // Live-vehicle layer, matching MBMT's live map exactly (MbmtLiveMap.tsx's own
      // 'mbmt-buses' setup): canvas-drawn top-down bus glyphs (busIconImageData, shared
      // with MBMT via busIcons.ts) registered as map images, driven by a symbol layer with
      // icon-rotate -- confirmed directly that a DOM-marker badge/glyph read as "a toy";
      // this is the same rendering technique the MBMT desk already uses and was asked for
      // by name. One set of images per style load (a restyle drops previously-registered
      // images along with everything else), hasImage guards each individually so this is
      // idempotent within a single style's lifetime too.
      for (const status of Object.keys(BUS_STATUS_STYLE) as VehicleStatus[]) {
        for (const model of BUS_MODELS) {
          const id = `nagpur-bus-${status}-${model}`
          if (!map.hasImage(id)) {
            const sty = BUS_STATUS_STYLE[status]
            map.addImage(id, busIconImageData(sty.fill, sty.edge, model), {
              pixelRatio: BUS_ICON_PIXEL_RATIO,
            })
          }
        }
      }
      busIconsRegistered.current = true

      if (!map.getSource(SRC_NAGPUR_BUSES)) {
        map.addSource(SRC_NAGPUR_BUSES, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      }
      if (!map.getLayer(LYR_NAGPUR_PULSE)) {
        map.addLayer({
          id: LYR_NAGPUR_PULSE,
          type: 'circle',
          source: SRC_NAGPUR_BUSES,
          filter: ['==', ['get', 'down'], true],
          paint: { 'circle-radius': 22, 'circle-color': '#F43F5E', 'circle-opacity': 0.18, 'circle-blur': 0.4 },
        })
      }
      if (!map.getLayer(LYR_NAGPUR_SELECTED_RING)) {
        map.addLayer({
          id: LYR_NAGPUR_SELECTED_RING,
          type: 'circle',
          source: SRC_NAGPUR_BUSES,
          filter: ['==', ['get', 'selected'], true],
          paint: {
            'circle-radius': 24,
            'circle-color': 'rgba(37,99,235,0.12)',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#2563EB',
          },
        })
      }
      if (!map.getLayer(LYR_NAGPUR_BUSES)) {
        map.addLayer({
          id: LYR_NAGPUR_BUSES,
          type: 'symbol',
          source: SRC_NAGPUR_BUSES,
          layout: {
            'icon-image': ['get', 'icon'],
            'icon-size': [
              'interpolate', ['linear'], ['zoom'],
              12, ['case', ['==', ['get', 'selected'], true], 0.42, 0.34],
              15, ['case', ['==', ['get', 'selected'], true], 0.58, 0.48],
              17, ['case', ['==', ['get', 'selected'], true], 0.78, 0.64],
            ],
            'icon-rotate': ['get', 'heading'],
            'icon-rotation-alignment': 'map',
            'icon-pitch-alignment': 'viewport',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        })
      }
      if (!map.getLayer(LYR_NAGPUR_BUS_LABELS)) {
        map.addLayer({
          id: LYR_NAGPUR_BUS_LABELS,
          type: 'symbol',
          source: SRC_NAGPUR_BUSES,
          minzoom: 14,
          layout: {
            'text-field': ['get', 'short'],
            'text-font': ['Instrument Sans Regular'],
            'text-size': 10.5,
            'text-offset': [0, 2.4],
            'text-anchor': 'top',
            'text-optional': true,
            'text-allow-overlap': false,
          },
          paint: { 'text-color': '#334155', 'text-halo-color': '#FFFFFF', 'text-halo-width': 1.4 },
        })
      }

      styleReady.current = true
      setMapLoaded(true)
      // setMapLoaded(true) is a no-op re-render on every restyle after the first (React
      // bails on an unchanged value), so it cannot be what re-triggers the selected-route
      // redraw effect below -- confirmed live (2026-09-25): a route drawn before the
      // google2d auto-promotion kicked in vanished the moment setStyle() wiped the map's
      // sources, and nothing ever redrew it because that effect had nothing in its
      // dependency array that actually changed value on a restyle. This counter does.
      setStyleVersion((v) => v + 1)

      if (!depotsPlaced) {
        depotsPlaced = true
        for (const depot of DEPOTS) {
          const el = document.createElement('div')
          el.className = 'flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-[#0F172A] text-[10px] font-bold text-white shadow-[0_1px_3px_rgba(15,23,42,0.4)]'
          el.textContent = 'D'
          el.title = depot.name
          new Marker({ element: el, anchor: 'center' })
            .setLngLat([depot.lon, depot.lat])
            .setPopup(new Popup({ offset: 16 }).setText(depot.name))
            .addTo(map)
        }
      }
    }
    map.on('load', onStyleLoad)
    map.on('style.load', onStyleLoad)

    // Same 3-strike tile-failure fallback MapCanvas.tsx uses for its own google2d default,
    // adapted to fall back to 'osm' (this page's safe default) instead of 'vector' (not a
    // valid mode here -- see this file's header comment).
    map.on('error', (e) => {
      console.error('[bus-route-map]', e.error?.message ?? e)
      const evt = e as unknown as { sourceId?: string }
      if (evt.sourceId !== 'basemap' || baseModeRef.current !== 'google2d') {
        google2dFailures.current = 0
        return
      }
      google2dFailures.current += 1
      if (google2dFailures.current < 3) return
      google2dFailures.current = 0
      useFleet.getState().addBanner({
        id: 'nagpur-google2d-failed', tone: 'warn',
        message: 'Google Maps tiles stopped loading — switched to OpenStreetMap.',
      })
      setBaseMode('osm')
    })

    const onSegmentClick = (e: MapLayerMouseEvent) => {
      const f = e.features?.[0]
      if (!f) return
      const p = f.properties as Record<string, unknown>
      const limit = p['limit_kph'] != null ? `${p['limit_kph']} kph` : '—'
      const p50 = p['observed_p50_kph'] != null ? `${p['observed_p50_kph']} kph` : '—'
      const p85 = p['observed_p85_kph'] != null ? `${p['observed_p85_kph']} kph` : '—'
      const n = p['sample_size'] != null ? `${p['sample_size']}` : '0'
      popupRef.current?.remove()
      popupRef.current = new Popup({ closeButton: true, maxWidth: '240px' })
        .setLngLat(e.lngLat)
        .setHTML(
          `<div style="font:12px 'JetBrains Mono',monospace">` +
          `<div style="font-weight:600;margin-bottom:4px">Segment ${p['from_seq']} → ${p['to_seq']}</div>` +
          `<div>Limit: ${limit} (${p['source'] ?? 'unknown'})</div>` +
          `<div>Observed p50: ${p50}</div>` +
          `<div>Observed p85: ${p85}</div>` +
          `<div style="color:#8794A7">n=${n} telemetry points</div>` +
          `</div>`,
        )
        .addTo(map)
    }
    map.on('click', LYR_BUS_SEGMENT_LINE, onSegmentClick)
    map.on('mouseenter', LYR_BUS_SEGMENT_LINE, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', LYR_BUS_SEGMENT_LINE, () => { map.getCanvas().style.cursor = '' })

    const onBusClick = (e: MapLayerMouseEvent) => {
      const f = e.features?.[0]
      if (!f) return
      const p = f.properties as Record<string, unknown>
      const vehicleId = String(p['id'])
      // The GeoJSON feature's own properties only ever carry what the per-frame RAF
      // loop below copies onto it for rendering (status/position/speed/soc/heading) --
      // never the EDMS duty fields (confirmed: synthesizing a row from `p` alone left
      // driver_name/schedule_code/current_trip permanently null here, even though the
      // board API itself returns them). busRowsRef.current is the SAME full BusBoardRow
      // list tryFocusVehicle() above already looks up by vehicle_id -- reading the real
      // row from there, not rebuilding a partial one from feature properties, is what
      // makes this popup match the Fleet drawer's own data.
      const row = busRowsRef.current.find((r) => r.vehicle_id === vehicleId)
      if (!row) return
      popupRef.current?.remove()
      popupRef.current = new Popup({ closeButton: true, maxWidth: '220px' })
        .setLngLat(e.lngLat)
        .setHTML(vehiclePopupHtml(row, p['status'] as VehicleStatus))
        .addTo(map)
    }
    map.on('click', LYR_NAGPUR_BUSES, onBusClick)
    map.on('mouseenter', LYR_NAGPUR_BUSES, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', LYR_NAGPUR_BUSES, () => { map.getCanvas().style.cursor = '' })

    return () => {
      popupRef.current?.remove()
      photorealRef.current?.destroy()
      photorealRef.current = null
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- construct once
  }, [])

  // Applies baseMode changes to the live map. setStyle() only after the CURRENT style has
  // finished loading (styleReady) -- calling it mid-load makes MapLibre discard its diff
  // and rebuild, same constraint MapCanvas.tsx's own syncStyle documents. Skips the initial
  // mount (map is already constructed with 'osm', nothing to apply yet).
  const appliedBaseMode = useRef<NagpurBaseMode>('osm')
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReady.current || appliedBaseMode.current === baseMode) return
    const basemap = rasterBasemap(baseMode) ?? rasterBasemap('osm')
    if (!basemap) return
    appliedBaseMode.current = baseMode
    styleReady.current = false // style.load (registered in the construct effect) sets it again
    map.setStyle(buildRasterStyle(basemap))
  }, [baseMode, mapLoaded])

  // Mirrors BasemapSwitch.tsx's toggle behaviour, scoped locally since this map has no
  // useFleet store dependency -- forcing one just for a single pill would be the wrong
  // trade against this component's existing self-containment. addPhotoreal itself is
  // dynamically imported, same as MapCanvas.tsx does -- the overwhelming majority of
  // sessions never touch this toggle, so it should not be in this route's main chunk.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return
    if (view3D && !photorealRef.current && mapsKey) {
      let cancelled = false
      void import('../map/photoreal').then(({ addPhotoreal }) => {
        if (cancelled || photorealRef.current) return
        photorealRef.current = addPhotoreal(map, mapsKey, {
          onLoad: () => {},
          onAttribution: (credits) => setAttribution(credits),
          onError: () => { /* a tile-load failure is transient (network, throttling) and
                              not worth surfacing as a page error -- the toggle stays on and
                              the next tile request just tries again */ },
        })
      })
      return () => { cancelled = true }
    }
    if (!view3D && photorealRef.current) {
      photorealRef.current.destroy()
      photorealRef.current = null
      setAttribution([])
    }
  }, [view3D, mapsKey, mapLoaded])

  // Fetch the selected route's detail on demand -- not prefetched for every route (see
  // detailCacheRef's own comment).
  useEffect(() => {
    if (!selectedRouteId || detailCacheRef.current.has(selectedRouteId)) return
    let alive = true
    fetchBusRouteDetail(selectedRouteId)
      .then((d) => {
        if (!alive) return
        detailCacheRef.current.set(selectedRouteId, d)
        setCacheVersion((v) => v + 1)
      })
      .catch(() => { /* selection just shows nothing on the map if its detail fails to
                        load -- the route list itself is unaffected */ })
    return () => { alive = false }
  }, [selectedRouteId])

  const selectedRoute = selectedRouteId ? routes.find((r) => r.route_id === selectedRouteId) ?? null : null
  const selectedDetail = selectedRouteId ? detailCacheRef.current.get(selectedRouteId) ?? null : null

  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReady.current) return
    setBusRouteData(map, selectedDetail)
    if (selectedDetail && selectedDetail.stops.length > 0) {
      const lons = selectedDetail.stops.map((s) => s.lon)
      const lats = selectedDetail.stops.map((s) => s.lat)
      map.fitBounds(
        [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
        { padding: 60, duration: 600 },
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedDetail derives from
    // selectedRouteId + cacheVersion; re-running on the id/version pair is what matters,
    // plus styleVersion so a restyle-triggered source wipe gets the selection redrawn too.
  }, [selectedRouteId, cacheVersion, styleVersion])

  // Latest data for the animation loop below, via refs so the rAF effect (mount-only)
  // never needs to restart just because a new poll, selection, or breakdown list came in
  // -- same structure as MbmtLiveMap.tsx's own busAnim*Ref set.
  const busRowsRef = useRef<BusBoardRow[]>([])
  const downSetRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    downSetRef.current = new Set((breakdowns ?? []).map((b) => b.vehicle_id))
  }, [breakdowns])
  const focusVehicleIdRef = useRef<string | null>(null)
  useEffect(() => { focusVehicleIdRef.current = focusVehicleId ?? null }, [focusVehicleId])
  const lastFocusedId = useRef<string | null>(null)

  const tryFocusVehicle = () => {
    const map = mapRef.current
    const wanted = focusVehicleIdRef.current
    if (!map || !wanted || wanted === lastFocusedId.current) return
    const row = busRowsRef.current.find((r) => r.vehicle_id === wanted)
    if (!row || row.lat == null || row.lon == null) return
    lastFocusedId.current = wanted
    const center: [number, number] = [row.lon, row.lat]
    map.flyTo({ center, zoom: Math.max(map.getZoom(), 14), duration: 600 })
    popupRef.current?.remove()
    popupRef.current = new Popup({ closeButton: true, maxWidth: '220px' })
      .setLngLat(center)
      .setHTML(vehiclePopupHtml(row, vehicleStatus(row, downSetRef.current.has(row.vehicle_id))))
      .addTo(map)
  }

  // Single shared animation loop -- dead-reckons every bus forward from its last real GPS
  // fix using coastBusPosition() (its own reported speed_kph/heading_deg), same technique
  // MBMT's live map uses, imported directly rather than re-derived so both pages move a
  // bus the same way. Rewrites the whole source every frame; MBMT's own comment on its
  // equivalent loop already established this is cheap enough at this fleet size with
  // collision placement disabled (icon-allow-overlap/icon-ignore-placement above) -- same
  // order of magnitude here (~250 buses vs MBMT's ~57), still comfortably under what
  // froze a tab in MBMT's own documented 3D-block case, and this layer is 2D-only (no
  // fill-extrusion re-tessellation cost to worry about). Coast is capped at
  // NAGPUR_MAX_COAST_S (module-level, see its own comment above) rather than MBMT's 12s.
  useEffect(() => {
    let raf = 0
    const tick = () => {
      raf = requestAnimationFrame(tick)
      const map = mapRef.current
      if (!map || !styleReady.current || !busIconsRegistered.current) return
      const src = map.getSource(SRC_NAGPUR_BUSES) as { setData: (d: object) => void } | undefined
      if (!src) return
      const now = Date.now()
      const downSet = downSetRef.current
      const sel = focusVehicleIdRef.current
      const features = busRowsRef.current
        .filter((r) => r.lat != null && r.lon != null)
        .map((r) => {
          const down = downSet.has(r.vehicle_id)
          const status = vehicleStatus(r, down)
          const ageS = r.updated_ts != null ? (now - r.updated_ts) / 1000 : 0
          const moving = status === 'moving'
          const pos = coastBusPosition(
            { lat: r.lat as number, lon: r.lon as number, speed_kph: r.speed_kph, heading_deg: r.heading_deg, moving },
            ageS,
            NAGPUR_MAX_COAST_S,
          )
          return {
            type: 'Feature' as const,
            properties: {
              id: r.vehicle_id,
              down,
              moving,
              stale: status === 'stale',
              status,
              selected: r.vehicle_id === sel,
              icon: `nagpur-bus-${status}-${busModel(undefined)}`,
              short: r.vehicle_id.slice(-4),
              depot_id: r.depot_id,
              updated_ts: r.updated_ts,
              speed_raw: r.speed_kph,
              soc_raw: r.soc_pct,
              heading: r.heading_deg ?? 0,
              lon: pos.lon,
              lat: pos.lat,
            },
            geometry: { type: 'Point' as const, coordinates: [pos.lon, pos.lat] },
          }
        })
      src.setData({ type: 'FeatureCollection', features })
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  // `board` is a prop now, not this component's own fetchBusBoard poll -- NagpurBusOpsShell
  // already polls the identical GET /api/bus/board every FLEET_POLL_MS (15s, its own
  // in-flight-guarded refresh()) for the Fleet drawer, so a second poll of the same
  // endpoint on the same cadence from this file duplicated every request for no reason
  // (CLAUDE.md's live-data section). busRowsRef stays a ref, not state -- the RAF tick
  // loop below reads it imperatively every frame, the same reasoning store.ts's own
  // header comment gives for not pushing position updates through React reconciliation.
  useEffect(() => {
    busRowsRef.current = board ?? []
    tryFocusVehicle()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tryFocusVehicle reads refs,
    // not props/state, so it does not need to be a dep.
  }, [board])

  // Click-to-select from the Fleet panel: the common case (a row already exists from an
  // earlier poll) resolves immediately here; the poll's own tryFocusVehicle() call above
  // covers the race where a vehicle is selected before its first poll response exists.
  useEffect(() => { tryFocusVehicle() }, [focusVehicleId])

  // True exactly when RouteTrafficView is covering the whole map -- drives both the
  // underlying MapLibre view's visibility and whether the basemap/photoreal pills (which
  // control that now-hidden view) make sense to show at all. Not used for the
  // RouteTrafficView render condition itself below -- that repeats the same clauses
  // inline so TypeScript can narrow selectedRoute/selectedDetail/mapsKey from nullable to
  // present, which a separately-computed boolean can't do for it.
  const trafficTakeoverActive = !routeListOpen && !!selectedRoute && !!selectedDetail && !!mapsKey

  return (
    <div className="absolute inset-0">
      <div ref={container} className={trafficTakeoverActive ? 'absolute inset-0 invisible' : 'absolute inset-0'} />

      {routeListOpen && (
        <RouteListOverlay
          routes={routes}
          onPick={(id) => { setSelectedRouteId(id); setRouteListOpen(false) }}
          onClose={() => setRouteListOpen(false)}
        />
      )}

      {!routeListOpen && selectedRoute && selectedDetail && mapsKey && (
        <RouteTrafficView
          route={selectedRoute}
          detail={selectedDetail}
          mapsKey={mapsKey}
          onClose={() => setSelectedRouteId(null)}
        />
      )}
      {!routeListOpen && selectedRoute && selectedDetail && !mapsKey && (
        <div className="absolute bottom-3 left-3 z-10 rounded-[8px] border border-[rgba(15,23,42,0.12)] bg-white px-3 py-2 text-[11px] text-[#94A3B8] shadow-[0_1px_3px_rgba(15,23,42,0.12)]">
          Live traffic needs Google Maps — not configured.
        </div>
      )}

      {/* On-demand only -- not a permanently-docked sidebar, per direct instruction.
          Hidden during the traffic takeover: RouteTrafficView's own header (same badge,
          name, close button) occupies this exact spot at that point. */}
      {!routeListOpen && !trafficTakeoverActive && (
        <button
          type="button"
          onClick={() => setRouteListOpen(true)}
          className="absolute left-3 top-3 z-10 flex items-center gap-1.5 rounded-[8px] border border-[rgba(15,23,42,0.12)] bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#475569] shadow-[0_1px_3px_rgba(15,23,42,0.12)] transition-colors hover:bg-[#F8FAFC]"
        >
          <List size={13} />
          {selectedRoute ? selectedRoute.display_name : `${routes.length} routes`}
        </button>
      )}

      {/* Base map + photoreal pills only apply to the (now hidden) MapLibre view --
          pointless clutter on top of RouteTrafficView's own Google map. */}
      {!trafficTakeoverActive && (
        <>
          {/* Base map: osm <-> google maps. Same pill visual language as
              cc/BasemapSwitch.tsx, built locally rather than reused directly since
              BasemapSwitch's full catalogue (satellite/terrain/vector) doesn't
              apply to this page -- see this file's header comment on why 'vector'
              specifically is never valid here. */}
          <div className="absolute right-3 top-3 z-10 flex overflow-hidden rounded-[8px] border border-[rgba(15,23,42,0.12)] bg-white text-[11px] font-medium shadow-[0_1px_3px_rgba(15,23,42,0.12)]">
            <button
              type="button"
              title="OpenStreetMap raster. Free, no session required."
              aria-pressed={baseMode === 'osm'}
              onClick={() => setBaseMode('osm')}
              className={
                baseMode === 'osm'
                  ? 'bg-[#2563EB] px-2.5 py-1.5 text-white'
                  : 'bg-white px-2.5 py-1.5 text-[#475569] hover:bg-[#F8FAFC]'
              }
            >
              osm
            </button>
            <button
              type="button"
              title={
                google2dAvailable
                  ? 'Google Maps roadmap tiles. Billed per session, needs internet.'
                  : 'Set GOOGLE_MAPS_API_KEY on the serving service to enable.'
              }
              aria-pressed={baseMode === 'google2d'}
              disabled={!google2dAvailable}
              onClick={() => setBaseMode('google2d')}
              className={
                baseMode === 'google2d'
                  ? 'bg-[#D97706] px-2.5 py-1.5 text-white disabled:cursor-not-allowed disabled:opacity-40'
                  : 'bg-white px-2.5 py-1.5 text-[#475569] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40'
              }
            >
              google maps
            </button>
          </div>

          {/* Photoreal 3D. Same pill visual language as cc/BasemapSwitch.tsx (amber when
              active = billed), kept local rather than reused directly since this map's
              other basemap pill above is also local, not the full BasemapSwitch
              catalogue. */}
          <div className="absolute right-3 top-11 z-10 rounded-[8px] border border-[rgba(15,23,42,0.12)] bg-white text-[11px] font-medium shadow-[0_1px_3px_rgba(15,23,42,0.12)]">
            <button
              type="button"
              title={
                photorealAvailable
                  ? 'Google Photorealistic 3D Tiles. Billed per session, needs internet. Right-drag to tilt.'
                  : 'Set GOOGLE_MAPS_API_KEY on the serving service to enable.'
              }
              aria-pressed={view3D}
              disabled={!photorealAvailable}
              onClick={() => setView3D((v) => !v)}
              className={
                view3D
                  ? 'rounded-[8px] bg-[#D97706] px-2.5 py-1.5 text-white disabled:cursor-not-allowed disabled:opacity-40'
                  : 'rounded-[8px] bg-white px-2.5 py-1.5 text-[#475569] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40'
              }
            >
              photoreal 3D
            </button>
            {view3D && attribution.length > 0 && (
              <div
                className="max-w-[260px] truncate px-1.5 pb-1 text-right text-[10px] text-[#94A3B8]"
                title={attribution.join(' · ')}
              >
                {attribution.join(' · ')}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
